import SV from 'server-vars';
import { FC, useEffect, useState, MouseEvent } from 'react';
import { trackEvent, TrackingObject } from 'dibs-tracking';
import ArrowRight from 'dibs-icons/exports/ArrowRight';
import { useFragment, graphql, useRelayEnvironment } from 'react-relay';
import { ZipCode } from 'dibs-address-form';
import { Button } from 'dibs-elements/exports/Button';
import { defineMessages, FormattedMessage, useIntl } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link';
import { BasicSelect } from 'dibs-elements/exports/BasicSelect';
import { ExpandingArea } from 'dibs-elements/exports/ExpandingArea';
import { setUserGeoInfo } from 'dibs-regional-info/exports/regionalInfoHelpers';
import { useUserGeoInfo } from 'dibs-regional-info/exports/useUserGeoInfo';
import { forceSetUserRegionalInfo } from 'dibs-regional-info/exports/setUserRegionalInfo';
import { editAccountPartialShippingAddress } from 'dibs-buyer-layout/exports/editAccountPartialShippingAddressMutation';

import { PdpSharedShippingAddress_viewer$key } from './__generated__/PdpSharedShippingAddress_viewer.graphql';
import { PdpSharedShippingAddress_user$key } from './__generated__/PdpSharedShippingAddress_user.graphql';

import classnames from 'classnames';
import styles from './PdpSharedShippingAddress.scss';

type Props = {
    user: PdpSharedShippingAddress_user$key | null;
    viewer: PdpSharedShippingAddress_viewer$key;
    onAddressUpdate?: (args: { userZipCode: string; userCountryCode: string }) => void;
    positioning?: 'sidebar' | 'seller_details';
};

const messages = defineMessages({
    countryPlaceholder: {
        id: 'pdp.PdpSharedShippingInfo.countryPlaceholder',
        defaultMessage: 'Country',
    },
});

export const PdpSharedShippingAddress: FC<Props> = ({
    viewer: viewerRef,
    user: userRef,
    onAddressUpdate,
    positioning,
}) => {
    const intl = useIntl();
    const relayEnv = useRelayEnvironment();

    const viewer = useFragment(
        graphql`
            fragment PdpSharedShippingAddress_viewer on Viewer {
                geography {
                    countries {
                        name
                        abbreviation
                    }
                }
            }
        `,
        viewerRef
    );

    const user = useFragment(
        graphql`
            fragment PdpSharedShippingAddress_user on User {
                serviceId
                ...useUserGeoInfo_user
            }
        `,
        userRef
    );

    const userGeoInfo = useUserGeoInfo(user);

    const [loading, setLoading] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<boolean>(false);

    const [countryCode, setCountryCode] = useState(userGeoInfo.countryCode);
    const [zipCode, setZipCode] = useState(userGeoInfo.zipCode);

    const countries = viewer?.geography?.countries || [];
    const country = countries.find(c => c?.abbreviation === userGeoInfo.countryCode);

    const trackingObj: TrackingObject = {
        locale: intl.locale,
        category: 'product',
        eventName: 'product_interaction',
        interaction_type: 'international',
        additional: {
            registrationStatus: user?.serviceId ? 'Registered' : 'Not Registered',
        },
    };

    useEffect(() => {
        if (!loading) {
            setCountryCode(userGeoInfo.countryCode);
            setZipCode(userGeoInfo.zipCode);
        }
    }, [loading, userGeoInfo.countryCode, userGeoInfo.zipCode]);

    const handleSubmit = async (event: MouseEvent): Promise<void> => {
        if (countryCode !== userGeoInfo.countryCode || zipCode !== userGeoInfo.zipCode) {
            setLoading(true);
            trackEvent(
                {
                    ...trackingObj,
                    label: `${positioning} - ${userGeoInfo.zipCode} to ${zipCode}`,
                    action: 'intl zip code complete',
                    step_interaction_name: 'intl zip code complete',
                },
                event
            );
            try {
                if (user?.serviceId) {
                    await editAccountPartialShippingAddress(relayEnv, {
                        userId: user?.serviceId,
                        address: {
                            zipCode,
                            country: countryCode,
                        },
                    });
                    await forceSetUserRegionalInfo({
                        relayEnv,
                        cookieDomain: SV.get('cookieDomain'),
                        userId: user?.serviceId || '',
                        useLocalStorage: true,
                    });
                } else {
                    await setUserGeoInfo({
                        environment: relayEnv,
                        userGeoInfo: {
                            countryCode,
                            zipCode,
                            incomeLevel: null,
                            regionsByZipCode: null,
                        },
                        useLocalStorage: true,
                    });
                }
                if (typeof onAddressUpdate === 'function') {
                    onAddressUpdate({
                        userZipCode: zipCode || '',
                        userCountryCode: countryCode || '',
                    });
                }
            } catch (e) {
                // eslint-disable-next-line
                console.error(e);
            }
            setLoading(false);
        }
    };

    if (!countryCode) {
        return null;
    }

    return (
        <div className={styles.wrapper} data-tn="pdp-shared-shipping-ship-to">
            <span
                className={classnames(styles.shippingText, {
                    [styles.lightWeight]: positioning === 'sidebar',
                })}
            >
                <FormattedMessage
                    id="pdp.PdpSharedShippingInfo.shipTo"
                    defaultMessage="Ship to {link}:"
                    values={{
                        link: (
                            <Link
                                onClick={(event: MouseEvent) => {
                                    if (!expanded) {
                                        trackEvent(
                                            {
                                                ...trackingObj,
                                                label: positioning,
                                                action: 'intl zip code entry',
                                                step_interaction_name: 'intl zip code entry',
                                            },
                                            event
                                        );
                                    }
                                    setExpanded(prev => !prev);
                                }}
                            >
                                <FormattedMessage
                                    id="pdp.PdpSharedShippingInfo.shipToLink"
                                    defaultMessage="{country}{hasZipCode, select, true {, {zipCode}} other {}}"
                                    values={{
                                        country: country?.name,
                                        zipCode: userGeoInfo.zipCode,
                                        hasZipCode: !!userGeoInfo.zipCode,
                                    }}
                                />
                            </Link>
                        ),
                    }}
                />
            </span>
            <ExpandingArea expanded={expanded}>
                <div className={styles.expandingArea}>
                    <div className={styles.select}>
                        <BasicSelect
                            hasAnimatedPlaceholder
                            name="country"
                            placeholder={intl.formatMessage(messages.countryPlaceholder)}
                            dataTn="pdp-shared-shipping-ship-to-country"
                            value={countryCode || ''}
                            options={countries.map(c => ({
                                label: c?.name || '',
                                value: c?.abbreviation || '',
                            }))}
                            size="large"
                            onChange={event => {
                                setCountryCode(event.target.value);
                            }}
                            disabled={loading}
                        />
                    </div>
                    <div className={styles.zipCodeWrapper}>
                        <ZipCode
                            wrapperClass={styles.zipCode}
                            value={zipCode || ''}
                            fieldSize="large"
                            onChange={({ value }) => {
                                setZipCode(value);
                            }}
                            isCountryUS={countryCode === 'US'}
                            disabled={loading}
                        />
                        <Button
                            className={styles.button}
                            type="secondary"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            <ArrowRight className={styles.arrowRight} />
                        </Button>
                    </div>
                </div>
            </ExpandingArea>
        </div>
    );
};
